/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2024
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { BillingApiEnum } from '../enums/api';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable, Subscriber } from 'rxjs';

@Injectable()
export class BillingApiService {
    constructor(
        private api: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Get retrieves stripe API key.
     * @param options
     */
    public getStripeKey(options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.get(BillingApiEnum.GET_STRIPE_KEY, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    // TODO this is a MOCK function to make it easier to follow the flow.
    // It return the same as
    /**
     * Get client secret key.
     * @param options
     */
    public getClientSecretKey(options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.post(BillingApiEnum.GET_CLIENT_SECRET_KEY, {}, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data.clientSecret);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get account.
     * @param options
     */
    public getAccount(options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.get(BillingApiEnum.GET_BILLING_ACCOUNT, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Create account, if the user/org doesn't have a stripe account we must create it.
     * @param data
     * @param options
     */
    public createAccount(data: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.post(BillingApiEnum.POST_BILLING_ACCOUNT, data, moduleAuth)
                .subscribe({
                    next: (data: any) => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get retrieves stripe API key.
     * @param options
     */
    public getAllProducts(options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.get(BillingApiEnum.GET_ALL_PRODUCTS, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data.products);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get retrieves stripe API key.
     * @param data
     * @param options
     */
    public createSubscription(data: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.post(BillingApiEnum.POST_SUBSCRIPTION, data, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get retrieves stripe API key.
     * @param options
     */
    public getSubscriptions(options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.get(BillingApiEnum.GET_SUBSCRIPTION, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get retrieves stripe API key.
     * @param id
     * @param options
     */
    public deleteSubscription(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.delete(BillingApiEnum.DELETE_SUBSCRIPTION(id), moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get retrieves stripe API key.
     * @param options
     */
    public addPaymentMethod(options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.post(BillingApiEnum.POST_PAYMENT_METHODS, {}, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get retrieves stripe API key.
     * @param options
     */
    public getPaymentMethods(options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.get(BillingApiEnum.GET_PAYMENT_METHODS, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get retrieves stripe API key.
     * @param id
     * @param options
     */
    public deletePaymentMethods(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.delete(BillingApiEnum.DELETE_PAYMENT_METHOD(id), moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get list of invoices.
     * @param options
     */
    public getInvoices(options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.get(BillingApiEnum.GET_INVOICES, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }
}
